/**
 * Slider control for a Shopify list filter whose values are numbers.
 *
 * The checkboxes Shopify renders are still what submit the filter — this only
 * ticks them. Dragging the handles selects every value in the chosen span.
 *
 * The handles SNAP to values that exist. Stepping by 1 looked smoother but let
 * a handle land on numbers no product has (CRI jumps 70 -> 80 -> 82 -> 83 ->
 * 90), which reads as a working filter returning nothing.
 */
class RangeSlider {
    constructor(wrapper) {
        this.wrapper = wrapper;

        this.slider = wrapper.querySelector('.range-slider');

        this.json = JSON.parse(
            wrapper.querySelector('.range-slider-json').textContent
        );

        this.filterName = wrapper.dataset.filter;

        // 'W' for wattage, empty for unitless values like CRI.
        this.suffix = wrapper.dataset.suffix || '';

        this.checkboxContainer = wrapper.parentElement.querySelector('ul');

        this.checkboxes = this.checkboxContainer
            ? [...this.checkboxContainer.querySelectorAll('input[type=checkbox]')]
            : [];

        if (!this.slider || !this.json.length) return;

        this.create();
    }

    create() {
        // Sort defensively: values arrive in Shopify's order, which is
        // alphabetical for text values ("100" sorts before "20").
        this.numbers = [...new Set(this.json.map((v) => Number(v.number)))]
            .filter((n) => !Number.isNaN(n))
            .sort((a, b) => a - b);

        if (this.numbers.length < 2) return; // nothing to drag between

        let startMin = this.numbers[0];
        let startMax = this.numbers[this.numbers.length - 1];

        const active = this.json.filter((v) => v.active);
        if (active.length) {
            const activeNumbers = active
                .map((v) => Number(v.number))
                .sort((a, b) => a - b);
            startMin = activeNumbers[0];
            startMax = activeNumbers[activeNumbers.length - 1];
        }

        noUiSlider.create(this.slider, {
            start: [startMin, startMax],
            connect: true,
            range: this.buildRange(),
            snap: true,
        });

        this.bind();
    }

    /**
     * noUiSlider positions values by percentage. Spacing each real value evenly
     * means a gap like 83 -> 90 takes the same track width as 80 -> 82, so
     * every value is equally reachable instead of a 7-wide stretch of dead
     * track next to a 2-wide one.
     */
    buildRange() {
        const range = {};
        const last = this.numbers.length - 1;

        this.numbers.forEach((n, i) => {
            if (i === 0) {
                range.min = n;
            } else if (i === last) {
                range.max = n;
            } else {
                range[`${(i / last) * 100}%`] = n;
            }
        });

        return range;
    }

    format(value) {
        return Math.round(value) + this.suffix;
    }

    bind() {
        this.slider.noUiSlider.on('update', (values) => {
            this.wrapper.querySelector('.slider-min').innerHTML = this.format(values[0]);
            this.wrapper.querySelector('.slider-max').innerHTML = this.format(values[1]);
        });

        this.slider.noUiSlider.on('change', (values) => {
            this.updateCheckboxes(values);
        });
    }

    updateCheckboxes(values) {
        const min = Math.round(values[0]);
        const max = Math.round(values[1]);

        this.checkboxes.forEach((input) => {
            input.checked = false;
        });

        this.json.forEach((item) => {
            if (item.number >= min && item.number <= max) {
                const input = this.checkboxes.find((cb) => cb.value == item.value);

                if (input) {
                    input.checked = true;
                }
            }
        });

        if (this.checkboxes.length) {
            this.checkboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
}

window.initRangeSliders = function () {
    document.querySelectorAll('.range-slider-wrapper').forEach((wrapper) => {
        wrapper.dataset.loaded = '';

        const el = wrapper.querySelector('.range-slider');

        // Facets re-render on every filter change, so tear down first or
        // noUiSlider throws on an already-initialised element.
        if (el?.noUiSlider) {
            el.noUiSlider.destroy();
        }

        new RangeSlider(wrapper);

        wrapper.dataset.loaded = 'true';
    });
};

document.addEventListener('DOMContentLoaded', () => {
    window.initRangeSliders();
});

document.addEventListener('shopify:section:load', () => {
    window.initRangeSliders();
});
